import React, { Component } from 'react';
import PropTypes from 'prop-types';
import LocaleCode from 'locale-code';
import { connect } from 'react-redux';
import { Button } from 'react-bootstrap';
import ordinal from 'ordinal-js';
import Fa from 'react-fontawesome';
import DateTimeDisplay from '../../../../components/date-time-display';
import NumberDisplay from '../../../../components/number-display';
import ContactNameRenderer from '../../../../components/table-cells/ContactNameRenderer';
import SurveyEmailRenderer from '../../../Dashboard/components/WinLossOpportunityTable/cells/SurveyEmailRenderer';
import ConfirmButton from '../../../../components/confirm-button';

import { transalteLanguageCode } from '../../../../util/helpers';

import styles from './styles.css';

class RequestRow extends Component {
  constructor(props) {
    super(props);
    this.state = {
      pausing: false,
    };
    this.requestChangeLanguage = this.requestChangeLanguage.bind(this);
    this.requestShowOutreachPlan = this.requestShowOutreachPlan.bind(this);
    this.requestChangeSenderEmail = this.requestChangeSenderEmail.bind(this);
    this.requestCancel = this.requestCancel.bind(this);
    this.requestPause = this.requestPause.bind(this);
  }

  requestShowOutreachPlan(e) {
    e.preventDefault();
    this.props.requestShowOutreachPlan(this.props.survey);
  }

  requestChangeLanguage(e) {
    e.preventDefault();
    if (this.state.pausing) {
      return;
    }
    this.props.requestChangeLanguage(this.props.survey);
  }

  requestChangeSenderEmail(e) {
    e.preventDefault();
    if (this.state.pausing) {
      return;
    }
    this.props.requestChangeSenderEmail(this.props.survey);
  }

  requestCancel() {
    if (this.state.pausing) {
      return;
    }
    this.props.requestCancelContact(this.props.survey);
  }

  requestPause() {
    if (this.state.pausing) {
      return;
    }
    this.setState({ pausing: true }, () => {
      const p = this.props.requestTogglePause(this.props.survey);
      p.then(() => {
        this.setState({ pausing: false });
      });
    });
  }

  renderOrdinal(num) {
    if (num === 0) {
      return num;
    }
    return (
      <span>{num}<sup>{ordinal.ordinalSuffix(num)}</sup></span>
    );
  }

  renderDatetime(dt) {
    return <DateTimeDisplay value={dt} format="dddd, MMMM DD, HH:mmA" />;
  }

  renderSuccessItem() {
    const { survey } = this.props;

    // complete the interview
    return (
      <span>
        <span className="text-success">Provided</span> feedback on
        {' '}
        {this.renderOrdinal(survey.completed_attempts)}
        {' '}
        request
        {survey.last_status_date !== null && <span>{' '}on {this.renderDatetime(survey.last_status_date, survey.time_zone)}</span>}
        .
      </span>
    );
  }

  renderUnsubscribedItem() {
    const { survey } = this.props;
    return (
      <span>
        <span className="text-danger">Declined</span> to provide feedback on
        {' '}
        {this.renderOrdinal(survey.completed_attempts)}
        {' '}
        request
        {survey.last_status_date !== null && <span>{' '}on {this.renderDatetime(survey.last_status_date, survey.time_zone)}</span>}
        .
      </span>
    );
  }

  renderTerminatedItem() {
    const { survey } = this.props;
    let reason = null;
    if (survey.terminated_reason && survey.terminated_reason.length > 0) {
      reason = (<span>&nbsp;due to: {survey.terminated_reason}</span>);
    }
    if (survey.completed_attempts === 0) {
      return (
        <span><span className="text-danger">Terminated</span>{reason}.</span>
      );
    }
    return (
      <span>
        <span className="text-danger">Terminated</span> on
        {' '}
        {this.renderOrdinal(survey.completed_attempts)}
        {' '}
        attempt
        {survey.last_status_date !== null && <span>{' '}on {this.renderDatetime(survey.last_status_date, survey.time_zone)}</span>}
        {reason}
        .
      </span>
    );
  }

  renderCompletedItem() {
    const { survey } = this.props;
    return (
      <span>
        <span className="text-warning">Completed</span> on
        {' '}
        {this.renderOrdinal(survey.completed_attempts)}
        {' '}
        request.
      </span>
    );
  }

  renderLastAttemptItem() {
    const { survey } = this.props;
    // last attempt
    return this.renderFeedbackStatus(survey);
  }

  renderEmptyStep() {
    return (
      <span>
        is currently in waiting area. (No steps created for this survey collector yet.)
      </span>
    );
  }

  renderOtherItem() {
    const { object, survey } = this.props;

    const nextEmailDate = survey.is_manual ? 'manually' : survey.next_email_date;
    const prefix = survey.is_manual ? 'will be sent ' : '';
    return (
      <span>
        {prefix}
        <SurveyEmailRenderer
          value={nextEmailDate}
          isNext
          object={object}
          data={survey}
          clickable
          requestShowEmail={this.props.requestShowEmail}
        />.
      </span>
    );
  }

  renderFeedbackStatus() {
    const { survey, canChangeSenderEmail } = this.props;
    // https://github.com/lumen-software/TruSales/issues/1748
    const totalAttempts = survey.max_attempts !== undefined && survey.max_attempts !== null ? survey.max_attempts : survey.total_attempts;

    const isLastAttempt = survey.completed_attempts === totalAttempts || survey.completed_attempts >= survey.total_attempts;
    const status = survey.contact_status; // last_status or contact_status???
    const canShowOutreachPlan = !isLastAttempt && (status === 'ACTIVE' || status === 'PAUSED');

    const inner = (
      <span>
        {this.renderOrdinal(survey.completed_attempts)}
        {' of '}
        {' '}
        <NumberDisplay value={survey.total_attempts} precision={0} /> attempted
      </span>
    );

    const attempts = canShowOutreachPlan ? (
      <span key="attempts">
        <a
          href=""
          onClick={this.requestShowOutreachPlan}
        >
          {inner}
        </a>
      </span>
    ) : (<span key="attempts">{inner}</span>);

    const currentLanguage = transalteLanguageCode(survey.language);
    let currentLanguageName = 'English';

    if (currentLanguage && LocaleCode.validateLanguageCode(currentLanguage)) {
      currentLanguageName = LocaleCode.getLanguageNativeName(currentLanguage);
    }

    const language = canShowOutreachPlan && (status === 'ACTIVE' || status === 'PAUSED') ? (
      <span key="language">
        in
        {' '}
        <a
          href=""
          onClick={this.requestChangeLanguage}
        >
          {currentLanguageName}
        </a>
      </span>
    ) : (<span key="language">in {currentLanguageName}</span>);

    const sender = survey.email_from && survey.email_from.name ? survey.email_from.name : 'Unknown';

    const behalf = canChangeSenderEmail && (status === 'ACTIVE' || status === 'PAUSED') ? (
      <span key="behalf">
        on behalf of
        {' '}
        <a
          href=""
          onClick={this.requestChangeSenderEmail}
        >
          {sender}
        </a>
      </span>
    ) : (
      <span key="behalf"> on behalf of {sender}</span>
    );

    return (
      <span>
        {attempts} {language} {behalf}
      </span>
    );
  }

  renderPausedItem() {
    const { survey } = this.props;
    return (
      <span>
        <span className="text-warning">Paused</span> on
        {' '}
        {this.renderOrdinal(survey.completed_attempts)}
        {' '}
        attempt
        {survey.last_status_date !== null && <span>{' '}on {this.renderDatetime(survey.last_status_date, survey.time_zone)}</span>}
        .
      </span>
    );
  }

  renderBouncedItem() {
    const { survey } = this.props;
    return (
      <span>
        <span className="text-warning">Bounced</span> on
        {' '}
        {this.renderOrdinal(survey.completed_attempts)}
        {' '}
        request
        {survey.last_status_date !== null && <span>{' '}on {this.renderDatetime(survey.last_status_date, survey.time_zone)}</span>}
        .
      </span>
    );
  }

  renderDisqualifiedItem() {
    const { survey } = this.props;
    return (
      <span>
        <span className="text-warning">Disqualified</span> on
        {' '}
        {this.renderOrdinal(survey.completed_attempts)}
        {' '}
        request
        {survey.last_status_date !== null && <span>{' '}on {this.renderDatetime(survey.last_status_date, survey.time_zone)}</span>}
        .
      </span>
    );
  }

  renderControls() {
    const {
      survey,
    } = this.props;
    const {
      pausing,
    } = this.state;
    const status = survey.contact_status; // last_status or contact_status???
    if (status !== 'ACTIVE' && status !== 'PAUSED' && status !== 'TERMINATED') { return null; }

    const label = (status === 'PAUSED' || status === 'TERMINATED') ? <span>Resume</span> : <span>Pause</span>;
    const icon = (status === 'PAUSED' || status === 'TERMINATED') ? 'play' : 'pause';

    return (
      <span className="btn-group">
          {(status === 'ACTIVE' || status === 'PAUSED' || status === 'TERMINATED') &&
            <ConfirmButton
              size="xs"
              onConfirm={this.requestPause}
            >
              <Fa name={(pausing ? 'spinner' : icon)} spin={pausing} /> {label}
            </ConfirmButton>
          }
          {(status === 'ACTIVE' || status === 'PAUSED') &&
            <Button
              bsStyle="default"
              bsSize="xs"
              disabled={pausing}
              onClick={this.requestCancel}
            >
              <Fa name="ban" /> Cancel
            </Button>
          }
      </span>
    );
  }

  render() {
    const {
      object, survey,
    } = this.props;
    const status = survey.last_status; // last_status or contact_status???
    const contactStatus = survey.contact_status;
    let comp = null;
    const totalAttempts = survey.max_attempts !== null && survey.max_attempts !== undefined ? survey.max_attempts : survey.total_attempts;

    if (status === 'SUCCEEDED') {
      comp = this.renderSuccessItem(survey);
    } else if (status === 'UNSUBSCRIBED' || contactStatus === 'UNSUBSCRIBED') {
      comp = this.renderUnsubscribedItem(survey);
    } else if (status === 'TERMINATED' || survey.contact_status === 'TERMINATED') {
      comp = this.renderTerminatedItem(survey);
    } else if (status === 'COMPLETED') {
      comp = this.renderCompletedItem(survey);
    } else if (contactStatus === 'PAUSED') {
      comp = this.renderPausedItem(survey);
    } else if (contactStatus === 'BOUNCED') {
      comp = this.renderBouncedItem(survey);
    } else if (contactStatus === 'DISQUALIFIED') {
      comp = this.renderDisqualifiedItem(survey);
    } else if ((survey.completed_attempts === totalAttempts && totalAttempts > 0) || (survey.completed_attempts >= survey.total_attempts && survey.total_attempts > 0)) {
      comp = this.renderLastAttemptItem(survey);
    } else if (totalAttempts === 0) {
      // they did not create any step for the SC yet
      comp = this.renderEmptyStep(survey);
    } else {
      comp = this.renderOtherItem(survey);
    }

    const title = survey.title && survey.title.length > 0 ? (<em>{survey.title}</em>) : null;
    const role = survey.role && survey.role.length > 0 ? (<span className="label label-warning">{survey.role}</span>) : (<span className="label label-default">No role</span>);
    const comma = title && role ? (<span>, </span>) : null;

    return (
      <div
        key={survey.id}
        className={`well well-sm clearfix ${styles.surveyItem}`}
      >
        <div>
          <div className="clearfix">
            <div className="pull-left">
              <p>
                <strong>
                  <ContactNameRenderer
                    value={`${survey.first_name} ${survey.last_name}`}
                    data={survey}
                  />
                </strong>
                {comma} {title} {role}
              </p>
            </div>
            <div className="pull-right">
              {this.renderControls()}
            </div>
          </div>
          <table className="table table-condensed">
            <tbody>
              <tr>
                <th width="170">
                  Outreach Plan:
                </th>
                <td>
                  {this.renderFeedbackStatus(survey)}
                </td>
              </tr>

              <tr>
                <th>
                Last Request:
                </th>
                <td>
                  <SurveyEmailRenderer
                    value={survey.last_email_date}
                    isNext={false}
                    object={object}
                    data={survey}
                    clickable
                    requestShowEmail={this.props.requestShowEmail}
                  />
                </td>
              </tr>

              <tr>
                <th>
                Next Request:
                </th>
                <td>
                  {comp}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  }
}

RequestRow.propTypes = {
  dispatch: PropTypes.func.isRequired,
  object: PropTypes.shape({
    opportunity_id: PropTypes.string,
    account_id: PropTypes.string,
  }).isRequired,
  survey: PropTypes.shape({
    id: PropTypes.string,
    completed_attempts: PropTypes.number,
    last_status_date: PropTypes.string,
    time_zone: PropTypes.string,
    language: PropTypes.string,
    terminated_reason: PropTypes.string,
    is_manual: PropTypes.bool,
    next_email_date: PropTypes.string,
    total_attempts: PropTypes.number,
    max_attempts: PropTypes.number,
    contact_status: PropTypes.string,
    last_status: PropTypes.string,
    role: PropTypes.string,
    title: PropTypes.string,
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    last_email_date: PropTypes.string,
    email_from: PropTypes.shape({
      name: PropTypes.string,
    }),
  }).isRequired,
  requestShowOutreachPlan: PropTypes.func,
  canChangeSenderEmail: PropTypes.bool,
  requestChangeSenderEmail: PropTypes.func,
  requestChangeLanguage: PropTypes.func,
  requestCancelContact: PropTypes.func,
  requestShowEmail: PropTypes.func,
  requestTogglePause: PropTypes.func,
  collectors: PropTypes.shape({}),
  showContactControl: PropTypes.bool,
};

RequestRow.defaultProps = {
  totalRequests: 0,
  isAccount: false,
  requestShowOutreachPlan: () => Promise.reject(new Error('need handler')),
  canChangeSenderEmail: false,
  requestChangeSenderEmail: () => {},
  requestChangeLanguage: () => {},
  requestCancelContact: () => {},
  requestShowEmail: () => {},
  requestTogglePause: () => {},
  collectors: {},
  showContactControl: false,
};

function mapStateToProps() {
  return {};
}

export default connect(mapStateToProps)(RequestRow);
